import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Anchor,
    Code,
    Group,
    Stack,
    Text,
} from '@mantine/core';
import {FlexiCopyButton} from '@flexinet/ui-components';

import {CONNECT_PROVIDER_DOCS_URL} from '../config';
import {
    permissionPolicyFor,
    RoleGrantSelection,
    trustPolicyFor,
} from '../services/rolePolicyBuilder';
// FNP-1400 — the checkboxes now live in one component, shared with the
// correction drawer, so connect and correct cannot describe permissions
// differently. See GrantChecklist for why.
import {GrantChecklist} from './GrantChecklist';

/**
 * FNP-1363 — THE ROLE, BUILT IN FRONT OF THE CUSTOMER.
 *
 * ⚠️ THIS IS THE TRUST MOMENT OF THE PRODUCT. A customer is being asked to
 * create an IAM role in their own AWS account that lets Flexi change their
 * DNS — which is to say, take their site and their mail offline. Hesitating is
 * the correct instinct. The panel therefore has to do two things that sound
 * opposed and are not: remove friction, and show enough that a careful person
 * can say yes.
 *
 * What it replaces was a sentence — "the Route 53 actions Flexi uses to read
 * and write your DNS records" — that names nothing. Someone security-minded
 * enough to pause is exactly the person who cannot accept that sentence, and
 * they had to leave for a documentation site to find out what it meant.
 *
 * ⚠️ THE TICK IS THE DECLARATION. The same checkbox that adds an action to the
 * pasted policy sets the capability Flexi stores, so the two cannot disagree.
 * That is FNP-1357 fixed structurally rather than patched: a connection can no
 * longer claim a write capability its role was never granted.
 */
export interface RoleBuilderPanelProps {
    readonly selection: RoleGrantSelection;
    readonly onChange: (selection: RoleGrantSelection) => void;

    /**
     * Flexi's own principal, for the trust policy the customer pastes.
     *
     * ⚠️ OPTIONAL BECAUSE THE API DOES NOT EXPOSE IT YET, not because it is
     * optional to the customer. The app must never invent it: a hardcoded
     * per-stage value is a second source of truth that drifts, and a trust
     * policy naming the wrong principal fails looking like the customer's
     * mistake. Absent, the panel says where the value comes from instead of
     * printing a guess someone might paste. Backend half of FNP-1363.
     */
    readonly flexiPrincipal?: string;
}

export const RoleBuilderPanel = ({
    selection,
    onChange,
    flexiPrincipal,
}: RoleBuilderPanelProps) => {

    const permissionPolicy = permissionPolicyFor(selection);

    return (
        <Stack gap={'md'} mt={'md'}>

            <Alert
                color={'gray'}
                variant={'light'}
                title={productCopy('What may {product} do in your account?')}
            >
                <Stack gap={'sm'}>
                    <GrantChecklist
                        selection={selection}
                        onChange={onChange}
                        intro={'Tick only what you are comfortable granting. The policy below is built from your choices — it is exactly what you will paste into AWS, and you can widen it later by re-validating.'}
                    />
                </Stack>
            </Alert>

            {/* ─── The permission policy, as it will be pasted ─── */}
            <Stack gap={'xs'}>
                <Group justify={'space-between'}>
                    <Text size={'sm'} fw={500}>
                        {I18n.get('Your permission policy')}
                    </Text>
                    <FlexiCopyButton
                        value={permissionPolicy}
                        data-testid={'permission-policy-copy'}
                    />
                </Group>
                <Code block data-testid={'permission-policy'}>
                    {permissionPolicy}
                </Code>
            </Stack>

            {/* ─── The trust policy ─── */}
            <Stack gap={'xs'}>
                <Group justify={'space-between'}>
                    <Text size={'sm'} fw={500}>
                        {I18n.get('Your trust policy')}
                    </Text>
                    {flexiPrincipal && (
                        <FlexiCopyButton
                            value={trustPolicyFor(flexiPrincipal)}
                            data-testid={'trust-policy-copy'}
                        />
                    )}
                </Group>

                {flexiPrincipal
                    ? (
                        <Code block data-testid={'trust-policy'}>
                            {trustPolicyFor(flexiPrincipal)}
                        </Code>
                    )
                    : (
                        // ⚠️ NO GUESS. Printing a placeholder principal invites a
                        // customer to paste it, producing a role that cannot be
                        // assumed and an error that blames their account.
                        <Text size={'sm'} c={'dimmed'} data-testid={'trust-policy-unavailable'}>
                            {I18n.get('The trust policy names Flexi\'s AWS principal, plus the external ID you will receive after saving. Both are shown on the connection once it is created.')}
                        </Text>
                    )}

                <Text size={'xs'} c={'dimmed'}>
                    {I18n.get('The external ID does not exist until the connection is saved, so the trust policy is completed in the second step — that is expected, not an error.')}
                </Text>
            </Stack>

            {/* The document carries what this panel deliberately does not
                reproduce: why certificates are issued in the customer's own
                account, and that the grants must persist for renewal. */}
            <Anchor
                size={'sm'}
                href={CONNECT_PROVIDER_DOCS_URL}
                target={'_blank'}
                rel={'noopener noreferrer'}
            >
                {I18n.get('The full trust policy and permission policy')}
            </Anchor>
        </Stack>
    );
};
